import { Button, List, ListItem, Modal, Stack, TextField, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@material-ui/icons/Search';
import Game from './Game';
import { MatchupInfo } from '../store/types';
import { setBoostedTeamsAction, setRemovedTeamsAction } from '../store/actions';
import { PriorityHigh } from '@mui/icons-material';

const GameControls = ({ projectionVersion, variant, boostOwnership, boostable, localStorageKey, onBoostApply} : { projectionVersion?: boolean, variant?: 'text' | 'outlined' | 'contained', boostOwnership?: boolean; boostable?: boolean, localStorageKey: string, onBoostApply: (boostedTeam: {[key:string]: number}, prevBoostedTeams?: {[key: string] : number}) => void }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState<string>('');
  const [internalBoostedTeam, setInternalBoostedTeam] = React.useState<{[key: string]: number}>({}); // [teamAbbrev: percent
  const [removedTeams, setRemovedTeams] = React.useState<string[]>([]); // [awayTeamAbbrev, homeTeamAbbrev
  const { selectedSlate, selectedSport, removedTeams: removedTeamsRedux, boostedTeams } = useSelector((state: any) => state.common);
  const dispatch = useDispatch();

  const handleRemove = (team: string) => {
    if(removedTeams.includes(team)) {
      setRemovedTeams(removedTeams.filter(t => t !== team));
    } else {
      setRemovedTeams([...removedTeams, team]);
      setInternalBoostedTeam({
        ...internalBoostedTeam,
        [team]: 0,
      })
    }
  }

  const onApply = () => {
    if(removedTeams?.length >= 0){
      dispatch(setRemovedTeamsAction(removedTeams));
      localStorage.setItem(localStorageKey, removedTeams?.join(','));
    }

    if(boostable) {
      localStorage.setItem(localStorageKey, JSON.stringify(internalBoostedTeam));
      dispatch(setBoostedTeamsAction(internalBoostedTeam));
    }
    if(boostOwnership) {
      localStorage.setItem(`boostOwnership-${localStorageKey}`, JSON.stringify(internalBoostedTeam));
      onBoostApply(internalBoostedTeam, boostedTeams);
      dispatch(setBoostedTeamsAction(internalBoostedTeam));
    }
    setOpen(false)
  }

  const onBoost = (team: string, percent: number) => {
    if(boostOwnership && percent === 0) {
      const boosted = internalBoostedTeam;
      delete boosted[team];
      setInternalBoostedTeam(boosted)
    } else {
      setInternalBoostedTeam({
        ...internalBoostedTeam,
        [team]: boostOwnership ? percent : percent / 100,
      })
    }
  }

  const onFilter = (matchupInfo: MatchupInfo) => {
    if(search) {
      return matchupInfo.awayTeamAbbrev.toLowerCase().includes(search.toLowerCase())
      || matchupInfo.homeTeamAbbrev.toLowerCase().includes(search.toLowerCase())
      || matchupInfo.awayTeamTitle.toLowerCase().includes(search.toLowerCase())
      || matchupInfo.homeTeamTitle.toLowerCase().includes(search.toLowerCase());
    }
    return true;
  }

  const onClear = () => {
    setRemovedTeams([]);
    setInternalBoostedTeam({});
  }


  React.useEffect(() => {
    setRemovedTeams(removedTeamsRedux);
  }, [removedTeamsRedux])

  React.useEffect(() => {
    setInternalBoostedTeam(boostedTeams);
  }, [boostedTeams])

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant={variant || "text"}
        sx={{
          color: 'inherit',
          backgroundColor: variant === "contained" ? '#000A16' : 'unset',
          textTransform: variant === "text" ? 'uppercase' : 'capitalize',
          textAlign: 'center',
          fontFamily: 'Inter',
          fontSize: '12px',
          fontStyle: 'normal',
          height: 36,
          fontWeight: 500,
          boxShadow: 'none !important',
          '&:hover': {
            backgroundColor: '#000A16',
          }
        }}>
        {isMobile ? 'Teams' : 'Team Controls'}
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontFamily: 'Inter',
        }}
      >
        <Stack
          direction="column"
          sx={{
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            backgroundColor: '#0A0A0A',
          }}
        >
          <Stack padding='15px 20px'>
            <Stack
              direction='row'
              sx={{
                color: 'white',
                fontSize: '20px',
                fontWeight: 400,
                '& span': {
                  fontWeight: 700,
                }
              }}
            >
              <span>Team Controls -</span> {selectedSlate.name} ({new Date(selectedSlate.startTime).toLocaleDateString([], { month: 'short', day: '2-digit', timeStyle: undefined })} - {new Date(selectedSlate.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} EST)
            </Stack>
            <Stack sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: 'white',
              my: '10px',
            }}>
              Use this feature to remove teams from the slate before creating a contest, or to boost team offenses after creating your contest.
            </Stack>
            <Stack>
              <TextField
                variant='outlined'
                placeholder='Search Teams'
                InputProps={{
                  startAdornment: <SearchIcon />,
                }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '6px',
                  color: 'white',
                  '& .MuiInputBase-root': {
                    paddingLeft: '8px',
                  },
                  '& input': {
                    color: 'white',
                    padding: 0,
                    paddingLeft: '8px',
                    height: '32px',
                  },
                  '& svg': {
                    color: 'white',
                  }
                }}
              />
            </Stack>
          </Stack>
          <Stack>
            <List>
              <ListItem sx={{
                borderBottom: '1px solid rgba(1, 90, 206, 0.30)',
                boxShadow: '0px -1px 0px 0px rgba(0, 0, 0, 0.12) inset',
                backgroundColor: 'rgba(0, 92, 206, 0.15)'
              }}>
                <Stack direction='row' justifyContent='space-between' width='100%' sx={{
                  color: 'rgba(255, 255, 255, 0.70)',
                  fontSize: '12px',
                  fontWeight: 800,
                }}>
                  <span>MATCHUPS</span>
                  {
                    boostable && (
                      <span>TEAM BOOST</span>
                    )
                  }
                  {
                    boostOwnership && (
                      <span>{selectedSport.sport?.toLowerCase() === 'mlb' ? 'BOOST BATTERS' : ' TEAM BOOST'}</span>
                    )
                  }
                </Stack>
              </ListItem>
              <Stack direction='column' maxHeight={360} overflow='scroll'>
                {
                  selectedSlate && selectedSlate?.matchupInfo && selectedSlate?.matchupInfo.filter((x: MatchupInfo) => onFilter(x)).map((matchupInfo: MatchupInfo) => (
                    <ListItem key={matchupInfo.awayTeamAbbrev + matchupInfo.homeTeamAbbrev}>
                      <Game  disableRemove={selectedSlate?.type?.toLowerCase() === 'showdown'} boostOwnership={boostOwnership} boostedValue={internalBoostedTeam} boostable={boostable} matchupInfo={matchupInfo} onRemove={handleRemove} removedTeams={removedTeams} onBoost={onBoost} />
                    </ListItem>
                  ))
                }
              </Stack>
            </List>
          </Stack>
          <Stack direction='row' padding="10px 12px" justifyContent='flex-end' borderTop='1px solid rgba(1, 90, 206, 0.30)'>
            {
              (selectedSlate?.matchupInfo?.length * 2) - removedTeams.length < 2 && (
                <Stack direction='row' alignItems='center' bgcolor='rgba(255, 0, 0, .1)' sx={{ border: '1px solid red', padding: '8px', borderRadius: '6px'}}>
                  <PriorityHigh sx={{ color: 'red', marginRight: '5px' }} />
                  <Stack sx={{ color: 'red', fontSize: '12px' }}>Please select at least 2 teams to continue</Stack>
                </Stack>
              )
            }
            <Button variant='text' sx={{ color: 'white', }} onClick={() => onClear()}>CLEAR SELECTIONS</Button>
            <Button
              onClick={onApply}
              disabled={(selectedSlate?.matchupInfo?.length * 2) - removedTeams.length < 2}
              sx={{
                padding: '10px 18px',
                borderRadius: '6px',
                backgroundColor: '#015ACE',
                color: 'white',
                height: '37px',
                '&:hover': {
                  backgroundColor: '#015ACE',
                }
              }}
            >
              Set Team Controls
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </>
  )
}

export default GameControls